import _ from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

export function useDebouncedValue<T>(
  initialValue: T,
  delay: number,
  onChange?: (newValue: T) => void // called when the debounced value changes.
  // NOTE: This should be a memoized function using `useCallback`.
  // Or else the debounced function will be recreated on every render and the debounce will not work.
  // There isn't an easy way to type this to enforce it.
): {
  value: T;
  debouncedSetValue: (newValue: T) => void;
  instantSetValue: (newValue: T) => void;
  flush: () => void;
} {
  const [value, setValue] = useState<T>(initialValue);
  const instantSetValue = useCallback(
    (newValue: T) => {
      setValue(newValue);
      onChange?.(newValue);
    },
    [onChange]
  );

  const debouncedOnChange = useMemo(
    () => (onChange ? _.debounce(onChange, delay) : () => {}),
    [onChange, delay]
  );

  // Flush pending debounced calls when the debounced function changes or on unmount
  // so that any in-flight changes are applied before the component is destroyed
  useEffect(() => {
    return () => {
      if (
        typeof debouncedOnChange === 'function' &&
        'flush' in debouncedOnChange
      ) {
        (debouncedOnChange as _.DebouncedFunc<(newValue: T) => void>).flush();
      }
    };
  }, [debouncedOnChange]);

  const flush = useCallback(() => {
    if (
      typeof debouncedOnChange === 'function' &&
      'flush' in debouncedOnChange
    ) {
      (debouncedOnChange as _.DebouncedFunc<(newValue: T) => void>).flush();
    }
  }, [debouncedOnChange]);

  const debouncedSetValue = useCallback(
    (newValue: T) => {
      setValue(newValue);
      debouncedOnChange(newValue);
    },
    [debouncedOnChange]
  );

  return useMemo(
    () => ({value, debouncedSetValue, instantSetValue, flush}),
    [value, debouncedSetValue, instantSetValue, flush]
  );
}
